import './index.scss';
import 'utils/input_mask';
import CreditCard from 'pages/checkout/components/checkout_payment/services/credit_card';
import Order from 'pages/checkout/components/checkout_payment/services/order';
import uniqBy from 'lodash/uniqBy';
import validator from 'utils/validator';
import { Controller } from 'utils/stimulus';
import { CpfVerify } from 'pages/checkout/utils/form-validation';
import { errorMapping } from 'pages/checkout/components/checkout_error_modal/errorMessages';

const { validate } = validator;

export default class TrialFormController extends Controller {
  static targets = [
    'offerName',
    'offerPrice',
    'offerId',
    'trialDays',
    'form',
    'issuer',
    'number',
    'expiration',
    'expirationMonth',
    'expirationYear',
    'name',
    'cpf',
    'cvv',
    'submitButton',
    'errorMessage'
  ];

  set loading(status) {
    const method = status ? 'add' : 'remove';
    this.submitButtonTarget.classList[method]('submit-button--loading');
  }

  get number() {
    const { value } = this.numberTarget;
    return value ? value.replace(/ /gi, '') : null;
  }

  get expiration() {
    return this.expirationTarget.value || '';
  }

  get expirationMonth() {
    return this.expirationMonthTarget.value;
  }

  set expirationMonth(value) {
    this.expirationMonthTarget.value = value;
  }

  get expirationYear() {
    return this.expirationYearTarget.value;
  }

  set expirationYear(value) {
    this.expirationYearTarget.value = value;
  }

  get expirationDate() {
    const parts = this.expiration.split('/');

    if (parts.length < 2) {
      return null;
    }
    const month = parseInt(parts[0]) - 1;

    if (month < 0 || month > 11) {
      return null;
    }
    const year = parseInt(`20${parts[1]}`);

    return new Date(year, month);
  }

  set issuer(value) {
    this.issuerTarget.value = value;
  }

  connect() {
    this.on('updateOffer', this.updateOffer.bind(this));
    this.setupValidator();
  }

  updateOffer(offer) {
    this.offerNameTarget.innerHTML = `Plano ${offer.offerName}`;
    this.offerPriceTarget.innerHTML = offer.offerPrice;
    this.offerIdTarget.value = offer.offerId;
    this.trialDaysTarget.innerHTML = `${gon.flags.trial_settings.days} dias`;

    this.element
      .querySelector('.subscription-icon')
      .setAttribute(
        'class',
        `subscription-icon subscription-icon--${offer.offerSubscription}`
      );
  }

  setupValidator() {
    this.formTarget.addEventListener('change', event => {
      if (event.target.matches('input')) {
        this.toggleSubmitButtonDisabledProp(this.formTarget);
      }
    });

    const errorHandler = field => {
      this.toggleSubmitButtonDisabledProp(field.closest('form'));
    };

    validator.init({
      afterShowError: errorHandler,
      afterRemoveError: errorHandler
    });
  }

  toggleSubmitButtonDisabledProp(form) {
    const submit = this.submitButtonTarget;

    if (!submit) {
      return;
    }

    const errors = form.querySelectorAll('.has-error');

    if (errors.length > 0 || this.isAnyRequiredFieldNotFilled(form)) {
      submit.setAttribute('disabled', 'disabled');
    } else {
      submit.removeAttribute('disabled');
    }
  }

  isAnyRequiredFieldNotFilled(form) {
    let result = true;

    form.querySelectorAll('[required]').forEach(function (item) {
      if (!result) {
        return;
      }

      if (!item.value) {
        result = false;
      }
    });

    return !result;
  }

  onExpirationDateChange() {
    const date = this.expirationDate;
    const isDateValid = this.validateExpirationDate();
    this.expirationMonth = isDateValid ? date.getMonth() + 1 : '';
    this.expirationYear = isDateValid ? date.getFullYear() : '';
  }

  onNameChange() {
    const isFullName = /^[a-zA-Z]+ [a-zA-Z]+/;
    const nameValue = this.nameTarget.value;

    if (!isFullName.test(nameValue)) {
      validate.showError(this.nameTarget, 'Adicione ao menos um sobrenome');
    } else {
      validate.removeError(this.nameTarget);
    }
  }

  onCpfVerify() {
    const cpfValue = this.cpfTarget.value;

    if (!CpfVerify(cpfValue)) {
      validate.showError(this.cpfTarget, 'CPF inválido');
    } else {
      validate.removeError(this.cpfTarget);
    }
  }

  async onNumberChange() {
    if (!this.validateNumber()) {
      return;
    }
    let err, issuer;

    // eslint-disable-next-line no-undef
    [err, issuer] = await withError(CreditCard.getIssuer(this.number));

    if (err) {
      return this.handleSubmitErrors(err);
    }

    this.issuer = issuer;
  }

  validateNumber() {
    if (this.number && this.number.trim() !== '' && this.number.length >= 6) {
      return true;
    }
    this.issuer = null;
    return false;
  }

  async onSubmit(event) {
    event.preventDefault();
    this.loading = true;
    this.hideErrorMessage();

    let err, data;

    // eslint-disable-next-line no-undef
    [err, data] = await withError(this.getSubmitData(event.target));

    if (err) {
      return this.handleSubmitErrors(err);
    }

    // eslint-disable-next-line no-undef
    [err] = await withError(Order.pay(null, data, true));

    if (err) {
      return this.handleSubmitErrors(err);
    }

    this.loading = false;
    this.trigger('trial-modal->goToSuccess');
  }

  async getSubmitData(form) {
    const data = $(form).serializeObject();
    data.payment = data.payment || {};
    data.payment.type = 'CreditCardPayment';
    data.payment.agreed_to_terms = 'on';
    data.payment.installments = 1;

    // eslint-disable-next-line no-undef
    const [err, token] = await withError(
      CreditCard.generateToken(this.formTarget)
    );

    if (err) {
      throw err;
    }

    data.card.token = token;

    return data;
  }

  validateExpirationDate() {
    const parts = this.expiration.split('/');
    const month = parseInt(parts[0]);
    const today = new Date();

    if (
      !this.expirationDate ||
      this.expirationDate < today ||
      month > 12 ||
      month < 1
    ) {
      setTimeout(
        () =>
          validate.showError(
            this.expirationTarget,
            'Data de vencimento inválida.'
          ),
        200
      );
      return false;
    }

    validate.removeError(this.expirationTarget);
    return true;
  }

  handleSubmitErrors(err) {
    this.loading = false;

    if (err.alert === true) {
      return this.showErrorMessage(err.message);
    }

    if (err === 'payment method not found') {
      return this.setFieldsErrors([CreditCard.getError('E301')]);
    }

    if (err.errors && err.errors instanceof Array) {
      const mappedErrors = err.errors
        .map(e => CreditCard.getError(e.code))
        .filter(x => !!x);
      const res = uniqBy(mappedErrors, x => x.field);
      return this.setFieldsErrors(res);
    }

    this.showErrorMessage();
  }

  setFieldsErrors(errors) {
    setTimeout(() => {
      errors.forEach(e => {
        const node = this[`${e.field}Target`];
        validator.validate.showError(node, e.message);
      });
    }, 100);
  }

  showErrorMessage(code) {
    const genericMessage =
      'Houve um erro ao processar sua compra. Tente novamente mais tarde ou use outro cartão.';
    const message = errorMapping[code]?.message || genericMessage;

    this.errorMessageTarget.textContent = message;
    this.errorMessageTarget.classList.add('error-message--show');
  }

  hideErrorMessage() {
    this.errorMessageTarget.classList.remove('error-message--show');
  }
}
